import { Text } from 'designSystem/components/Text'
import {
  removeAgentPen,
  useAgentViewState,
} from 'features/timeSeries/agentTab/agentView'
import type {
  ChatPart,
  ChatTurn,
} from 'features/timeSeries/agentTab/chatStream'
import { captionParts } from 'features/timeSeries/agentTab/toolCaption'
import React from 'react'
import styled from 'styled-components'

import {
  FitCard,
  FocusCard,
  KpiCard,
  PenCard,
  RolesCard,
  SavedCard,
} from './BuildCard'
import { TagLinkedMarkdown } from './TagLinkedMarkdown'
import { ToolCard } from './ToolCard'

const UserBubble = styled.div`
  align-self: flex-end;
  max-width: 85%;
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.625rem 0.625rem 0.125rem 0.625rem;
  padding: 0.5rem 0.75rem;
  white-space: pre-wrap;
`

const AssistantBlock = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Author = styled(Text)`
  display: block;
  margin-bottom: 0.125rem;
`

const PartView: React.FC<{ part: ChatPart; caption?: string }> = ({
  part,
  caption,
}) => {
  switch (part.kind) {
    case 'text':
      return (
        <Text as='div' variant='body' leading='relaxed'>
          <TagLinkedMarkdown>{part.text}</TagLinkedMarkdown>
        </Text>
      )
    case 'status':
      return (
        <Text as='div' variant='caption' color='tertiary' italic>
          {part.text}
        </Text>
      )
    case 'roles':
      return <RolesCard part={part} />
    case 'kpi':
      return <KpiCard part={part} />
    case 'fit':
      return <FitCard part={part} />
    case 'saved':
      return <SavedCard part={part} />
    case 'focus':
      return <FocusCard part={part} />
    case 'pen':
      return <PenView part={part} />
    case 'tool':
      return <ToolCard part={part} caption={caption} />
  }
}

/** A pen card only offers removal while its pen is actually on the chart. */
const PenView: React.FC<{ part: Extract<ChatPart, { kind: 'pen' }> }> = ({
  part,
}) => {
  const { pens } = useAgentViewState()
  return (
    <PenCard
      part={part}
      onChart={pens.some((pen) => pen.id === part.penId)}
      onRemove={() => removeAgentPen(part.penId)}
    />
  )
}

/**
 * Memoized on the turn object's identity: streaming replaces only the LAST
 * turn's object, so earlier turns never re-render — without this, every
 * streamed event re-rendered the whole transcript (visible jank).
 */
const MemoPartView = React.memo(PartView)

const TurnViewBase: React.FC<{ turn: ChatTurn }> = ({ turn }) => {
  if (turn.role === 'user') {
    return (
      <UserBubble>
        {/* Conversations are shared, so a message says whose it was. Only a
            stored turn carries one; the one you just typed does not need it. */}
        {turn.author && (
          <Author variant='caption' color='tertiary'>
            {turn.author}
          </Author>
        )}
        <Text variant='body'>
          {turn.parts[0]?.kind === 'text' ? turn.parts[0].text : ''}
        </Text>
      </UserBubble>
    )
  }
  return (
    <AssistantBlock>
      {captionParts(turn.parts).map((item, index) => (
        <MemoPartView key={index} part={item.part} caption={item.caption} />
      ))}
    </AssistantBlock>
  )
}

export const TurnView = React.memo(TurnViewBase)
